/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';

import type { CommonProps } from '../../_common/types';
import { Presence } from '../presence';
import { formatThinkingDuration } from './thinking-duration-formatter';

type ThinkingProps = {
  /** Whether the AI is thinking.
   * If true, will render a thinking presence icon and the text "Thinking".
   * If false, will render the duration of the thinking.
   */
  isThinking?: boolean;
  /** The duration of the thinking in milliseconds */
  thinkingMs?: number;
};

/**
 * The component is used to display the thinking state of an LLM.
 * If the `isThinking` prop is true, it will render a thinking presence icon and the text "Thinking".
 * If the `isThinking` prop is false, it will render the duration of the thinking.
 *
 * @alpha - Changes to this component may be breaking.
 */
const Thinking = ({
  className,
  isThinking = true,
  style,
  htmlAttributes,
  ref,
  thinkingMs = 1000,
  ...restProps
}: CommonProps<'div', ThinkingProps>) => {
  const classes = classNames(
    'n-flex n-flex-col n-items-start n-gap-token-12',
    className,
  );

  const { value: thinkingDuration, unit } = formatThinkingDuration(thinkingMs);

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {isThinking === true ? (
        <>
          <Presence isThinking={isThinking} className="n-size-token-32" />
          <span className="n-subheading-small n-text-neutral-text-default">
            Thinking
          </span>
        </>
      ) : (
        <span className="n-subheading-small n-text-neutral-text-weak">
          Thought{' '}
          <span className="n-font-normal">
            for {thinkingDuration} {unit}
          </span>
        </span>
      )}
    </div>
  );
};

export { Thinking };
